import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import useMap from '@alltrails/maps/hooks/useMap';
import type { AlgoliaSearchResult } from '@alltrails/types/AlgoliaResults';
import { handleAlgoliaResultSelect } from '@/utils/handleAlgoliaResultSelect';
import { useAppDispatch } from '@/utils/redux';

type UseAlgoliaResultSelectParams = {
  navigateToPOI?: boolean;
  navigateToPlace?: boolean;
  navigateToTrail?: boolean;
  onResultSelect?: (result: AlgoliaSearchResult) => void;
};

const useAlgoliaResultSelect = ({
  navigateToPOI = false,
  navigateToPlace = false,
  navigateToTrail = false,
  onResultSelect
}: UseAlgoliaResultSelectParams) => {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const searchParams = useSearchParams();
  const map = useMap();
  const languageRegionCode = useLanguageRegionCode();
  const isMobileBrowser = useSelector((state: { context: { mobileBrowser: boolean } }) => state.context?.mobileBrowser);
  const shouldNavigateToTrail = navigateToTrail && !isMobileBrowser;

  const handleResultSelect = useCallback(
    (result?: AlgoliaSearchResult) => {
      if (result) {
        handleAlgoliaResultSelect(
          result,
          languageRegionCode,
          dispatch,
          router,
          searchParams,
          map,
          onResultSelect,
          navigateToPOI,
          navigateToPlace,
          shouldNavigateToTrail
        );
      }
    },
    [languageRegionCode, dispatch, router, searchParams, map, onResultSelect, navigateToPOI, navigateToPlace, shouldNavigateToTrail]
  );

  return handleResultSelect;
};

export default useAlgoliaResultSelect;
